'use client';

import { useState, useEffect } from 'react';
import { Share2, Twitter, Facebook, MessageCircle, Send, Copy, Check } from 'lucide-react';

interface ResultShareButtonProps {
  result: any;
  imageUrl?: string;
}

export const ResultShareButton = ({ result, imageUrl }: ResultShareButtonProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [randomPercentage, setRandomPercentage] = useState(95);
  const [shareImageUrl, setShareImageUrl] = useState<string>('');

  useEffect(() => {
    // 生成92到98之间的随机数，与ResultCard保持一致
    const randomNum = Math.floor(Math.random() * (98 - 92 + 1)) + 92;
    setRandomPercentage(randomNum);
  }, [result]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = `I got a beauty score of ${result?.score}/10!`;
  const congratsText = `Congratulations! You're in the Top ${randomPercentage}%`;
  const shareDescription = `${congratsText} Check out my AI attractiveness test result! Try it yourself at AI Attractiveness Test.`;

  // 生成图片blob (用于Web Share API)
  const generateImageBlob = async (): Promise<Blob | null> => {
    if (!result) return null;

    try {
      const canvas = document.createElement('canvas');
      const width = 720;
      const height = 480;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) return null;

      // 设置背景渐变
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#8B5CF6');
      gradient.addColorStop(1, '#EC4899');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // 添加半透明白色覆盖层
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.fillRect(0, 0, width, height);

      // 设置文字样式
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 标题
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillText(congratsText, width / 2, height / 2 - 60);

      // 分数
      ctx.font = 'bold 64px Arial, sans-serif';
      ctx.fillText(`${result.score}/10`, width / 2, height / 2);

      // 底部文字
      ctx.font = '24px Arial, sans-serif';
      ctx.fillText('AI Attractiveness Test', width / 2, height / 2 + 60);

      // 网站链接
      ctx.font = '18px Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillText('aiattractiveness.com', width / 2, height / 2 + 100);

      // 转换为blob
      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 0.9);
      });
    } catch (error) {
      console.error('Error generating image blob:', error);
      return null;
    }
  };

  // 生成分享图片 (上传到图床)
  const generateShareImage = async () => {
    try {
      const blob = await generateImageBlob();
      if (!blob) return null;

      // 上传到图片服务
      const formData = new FormData();
      formData.append('smfile', blob, 'share-result.png');
      formData.append('format', 'json');

      const uploadResponse = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      const uploadData = await uploadResponse.json();

      if (uploadResponse.ok && uploadData.url) {
        return uploadData.url;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error generating share image:', error);
      return null;
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const shareToTwitter = async () => {
    // 优先尝试使用Web Share API (移动端)
    if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
      try {
        // 生成图片blob
        const imageBlob = await generateImageBlob();
        if (imageBlob) {
          const file = new File([imageBlob], 'beauty-score.png', { type: 'image/png' });
          await navigator.share({
            title: shareTitle,
            text: shareDescription,
            files: [file]
          });
          return;
        }
      } catch (error) {
        console.log('Web Share API failed, falling back to URL sharing');
      }
    }

    // 桌面端或Web Share API不可用时，使用图床链接
    let imageUrl = shareImageUrl;
    if (!imageUrl) {
      imageUrl = await generateShareImage() || '';
      setShareImageUrl(imageUrl);
    }

    const text = encodeURIComponent(`${shareTitle} ${shareDescription}`);
    const url = encodeURIComponent(shareUrl);
    let twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;

    // 在文本中包含图片链接
    if (imageUrl) {
      const textWithImage = encodeURIComponent(`${shareTitle} ${shareDescription}\n\n📸 ${imageUrl}`);
      twitterUrl = `https://twitter.com/intent/tweet?text=${textWithImage}&url=${url}`;
    }

    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const shareToFacebook = async () => {
    // 生成分享图片
    let imageUrl = shareImageUrl;
    if (!imageUrl) {
      imageUrl = await generateShareImage() || '';
      setShareImageUrl(imageUrl);
    }

    const url = encodeURIComponent(shareUrl);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = async () => {
    // 优先尝试使用Web Share API (移动端)
    if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
      try {
        const imageBlob = await generateImageBlob();
        if (imageBlob) {
          const file = new File([imageBlob], 'beauty-score.png', { type: 'image/png' });
          await navigator.share({
            title: shareTitle,
            text: shareDescription,
            files: [file]
          });
          return;
        }
      } catch (error) {
        console.log('Web Share API failed, falling back to URL sharing');
      }
    }

    // 桌面端或Web Share API不可用时，使用图床链接
    let imageUrl = shareImageUrl;
    if (!imageUrl) {
      imageUrl = await generateShareImage() || '';
      setShareImageUrl(imageUrl);
    }

    let text = `${shareTitle} ${shareDescription} ${shareUrl}`;
    if (imageUrl) {
      text = `${shareTitle} ${shareDescription}\n\n📸 ${imageUrl}\n\n${shareUrl}`;
    }

    const encodedText = encodeURIComponent(text);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedText}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToTelegram = async () => {
    // 生成分享图片
    let imageUrl = shareImageUrl;
    if (!imageUrl) {
      imageUrl = await generateShareImage() || '';
      setShareImageUrl(imageUrl);
    }

    let text = shareTitle;
    if (imageUrl) {
      text = `${shareTitle}\n\n📸 ${imageUrl}`;
    }

    const encodedText = encodeURIComponent(text);
    const url = encodeURIComponent(shareUrl);
    const telegramUrl = `https://t.me/share/url?url=${url}&text=${encodedText}`;
    window.open(telegramUrl, '_blank');
  };

  // 生成预览图片用于下载
  const generatePreviewImage = () => {
    if (!result || !imageUrl) return '';

    // 这里应该返回处理后的图片URL，包含分数等信息
    // 暂时返回原图片URL
    return imageUrl;
  };

  const previewImage = generatePreviewImage();

  return (
    <div className="w-full">
      {/* 主要按钮行 - 响应式布局 */}
      <div className="flex justify-center md:justify-start items-center gap-3 md:gap-4 w-full">
        {/* Download Button */}
        <button
          onClick={() => {
            const link = document.createElement('a');
            link.download = `beauty-score-${result.score.toFixed(1)}.jpg`;
            link.href = previewImage;
            link.click();
          }}
          className="inline-flex items-center justify-center px-4 py-2 md:px-6 md:py-3 bg-violet-600 text-white rounded-lg md:rounded-xl font-medium text-sm md:text-base min-w-[120px] md:min-w-[140px]
                     transform transition-all duration-300 hover:bg-violet-700 hover:scale-105 hover:shadow-lg
                     focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-opacity-50"
        >
          <span>Download</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>

        {/* Share Button */}
        <div className="flex flex-col md:flex-row md:items-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center justify-center px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg md:rounded-xl font-medium text-sm md:text-base min-w-[120px] md:min-w-[140px]
                       transform transition-all duration-300 hover:scale-105 hover:shadow-lg
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            <Share2 className={`h-4 w-4 md:h-5 md:w-5 mr-2 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
            <span>Share</span>
          </button>

          {/* Desktop: Horizontal expansion to the right */}
          <div className={`hidden md:flex overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-w-xs opacity-100 ml-4' : 'max-w-0 opacity-0 ml-0'
          }`}>
            <div className="flex items-center gap-3 whitespace-nowrap">
              {/* Twitter */}
              <button
                onClick={shareToTwitter}
                className="w-12 h-12 bg-[#1DA1F2] hover:bg-[#1a91da] text-white rounded-full flex items-center justify-center
                           transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
                title="Share on Twitter"
              >
                <Twitter size={20} />
              </button>

              {/* Facebook */}
              <button
                onClick={shareToFacebook}
                className="w-12 h-12 bg-[#4267B2] hover:bg-[#365899] text-white rounded-full flex items-center justify-center
                           transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
                title="Share on Facebook"
              >
                <Facebook size={20} />
              </button>

              {/* WhatsApp */}
              <button
                onClick={shareToWhatsApp}
                className="w-12 h-12 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full flex items-center justify-center
                           transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
                title="Share on WhatsApp"
              >
                <MessageCircle size={20} />
              </button>

              {/* Telegram */}
              <button
                onClick={shareToTelegram}
                className="w-12 h-12 bg-[#0088cc] hover:bg-[#0077b3] text-white rounded-full flex items-center justify-center
                           transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
                title="Share on Telegram"
              >
                <Send size={20} />
              </button>

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="w-12 h-12 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center
                           transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl border border-gray-200"
                title={copied ? "Copied!" : "Copy Link"}
              >
                {copied ? (
                  <Check size={20} className="text-green-600" />
                ) : (
                  <Copy size={20} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Vertical expansion below the button row */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
        isExpanded ? 'max-h-20 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'
      }`}>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {/* Twitter */}
          <button
            onClick={shareToTwitter}
            className="w-10 h-10 bg-[#1DA1F2] hover:bg-[#1a91da] text-white rounded-full flex items-center justify-center
                       transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
            title="Share on Twitter"
          >
            <Twitter size={18} />
          </button>

          {/* Facebook */}
          <button
            onClick={shareToFacebook}
            className="w-10 h-10 bg-[#4267B2] hover:bg-[#365899] text-white rounded-full flex items-center justify-center
                       transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
            title="Share on Facebook"
          >
            <Facebook size={18} />
          </button>

          {/* WhatsApp */}
          <button
            onClick={shareToWhatsApp}
            className="w-10 h-10 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full flex items-center justify-center
                       transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
            title="Share on WhatsApp"
          >
            <MessageCircle size={18} />
          </button>

          {/* Telegram */}
          <button
            onClick={shareToTelegram}
            className="w-10 h-10 bg-[#0088cc] hover:bg-[#0077b3] text-white rounded-full flex items-center justify-center
                       transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
            title="Share on Telegram"
          >
            <Send size={18} />
          </button>

          {/* Copy Link */}
          <button
            onClick={handleCopyLink}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center
                       transform transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl border border-gray-200"
            title={copied ? "Copied!" : "Copy Link"}
          >
            {copied ? (
              <Check size={18} className="text-green-600" />
            ) : (
              <Copy size={18} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
